import { useRouter } from 'vue-router'
import { AuthenticationError } from '../services/api.js'
import { useLinkflareAuth } from './useAuth.js'

export function useApiErrorHandler() {
  const router = useRouter()
  const { handleSignOut: authSignOut } = useLinkflareAuth()

  const clearApiKey = () => {
    try {
      localStorage.removeItem('linkflare_api_key')
    } catch (e) {
      console.warn('Failed to remove api key from localStorage', e)
    }
    // Also clear the domain-wide cookie used by browser extensions
    try {
      const isLocalhost = location.hostname === 'localhost' || location.hostname === '127.0.0.1'
      const cookieDomain = isLocalhost ? '' : '; domain=.linkflare.io'
      const secure = isLocalhost ? '' : '; Secure'
      document.cookie = `linkflare_api_key=${cookieDomain}; path=/; max-age=0; SameSite=None${secure}`
    } catch (e) {
      console.warn('Failed to clear api key cookie', e)
    }
  }

  const handleApiError = async (error, context = 'API call') => {
    console.error(`${context} failed:`, error)
    
    // Handle authentication errors specifically
    if (error instanceof AuthenticationError && error.status === 401) {
      clearApiKey()

      let signedOut = false
      if (typeof authSignOut === 'function') {
        try {
          signedOut = await authSignOut()
        } catch (signOutError) {
          console.error('Automatic sign-out failed:', signOutError)
        }
      }

      if (!signedOut) {
        router.push('/sign-in')
      }

      return true // Indicates error was handled
    }
    
    return false // Indicates error was not handled
  }

  const wrapApiCall = async (apiCallFn, context = 'API call') => {
    try {
      return await apiCallFn()
    } catch (error) {
      const handled = await handleApiError(error, context)
      if (!handled) {
        // Re-throw the error if it wasn't a handled auth error
        throw error
      }
      return undefined
    }
  }

  return {
    handleApiError,
    wrapApiCall,
    clearApiKey
  }
}